import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import '../styles/AdvancedROIMetrics.css';

const AdvancedROIMetrics = ({ formData }) => {
  const { subscription } = useSubscription();
  const [isExpanded, setIsExpanded] = useState(false);

  const calculateAdvancedMetrics = () => {
    const price = parseFloat(formData.propertyPrice) || 0;
    const monthlyRent = parseFloat(formData.monthlyRent) || 0;
    const downPaymentPercent = parseFloat(formData.downpaymentPercentage) / 100 || 1;
    const yearlyTax = parseFloat(formData.propertyTax) || 0;
    const yearlyInsurance = parseFloat(formData.yearlyInsurance) || 0;
    const monthlyHOA = parseFloat(formData.monthlyHOA) || 0;
    const yearlyMaintenance = parseFloat(formData.yearlyMaintenance) || 0;
    const vacancyRate = parseFloat(formData.vacancyRate) || 0;
    const managementFeePercentage = parseFloat(formData.managementFeePercentage) || 0;
    const additionalExpenses = (parseFloat(formData.additionalExpenses) || 0) * 12;
    
    console.log('=== ADVANCED METRICS DEBUG ===');
    console.log('Form Data:', {
      price, monthlyRent, downPaymentPercent, yearlyTax, yearlyInsurance, 
      monthlyHOA, yearlyMaintenance, vacancyRate, managementFeePercentage, additionalExpenses
    });

    if (price === 0) return null;

    const annualRent = monthlyRent * 12;
    // Match FormPage calculation exactly
    const grossIncome = monthlyRent * 12 - (monthlyRent / 30) * (vacancyRate / 100) * 365;
    const totalOperatingExpenses = yearlyTax + monthlyHOA * 12 + yearlyInsurance + (managementFeePercentage / 100) * monthlyRent * 12 + yearlyMaintenance;
    const netOperatingIncome = grossIncome - totalOperatingExpenses;
    
    const downPayment = price * downPaymentPercent;
    const annualCashFlow = netOperatingIncome - additionalExpenses;
    const totalCashReturn = downPayment > 0 ? (annualCashFlow / downPayment) * 100 : 0;
    
    console.log('Calculations:', {
      grossIncome, totalOperatingExpenses, netOperatingIncome,
      annualCashFlow, downPayment, totalCashReturn, 
      totalCashReturnFormatted: totalCashReturn.toFixed(2)
    });
    
    const loanAmount = price - downPayment;

    // Advanced Metrics
    const grossRentMultiplier = price / annualRent;
    const rentToPrice = (monthlyRent / price) * 100;
    const operatingExpenseRatio = (totalOperatingExpenses / grossIncome) * 100;
    const debtServiceCoverageRatio = additionalExpenses > 0 ? netOperatingIncome / additionalExpenses : 'N/A';
    
    // Cash Flow Analysis  
    const monthlyCashFlow = (annualCashFlow) / 12;
    
    // Break-even Analysis
    const breakEvenOccupancy = ((totalOperatingExpenses + additionalExpenses) / annualRent) * 100;
    
    // Return Metrics (already calculated above)
    const returnOnInvestment = totalCashReturn;

    return {
      grossRentMultiplier: grossRentMultiplier.toFixed(2),
      rentToPrice: rentToPrice.toFixed(2),
      operatingExpenseRatio: operatingExpenseRatio.toFixed(1),
      debtServiceCoverageRatio: typeof debtServiceCoverageRatio === 'number' ? debtServiceCoverageRatio.toFixed(2) : debtServiceCoverageRatio,
      monthlyCashFlow: monthlyCashFlow.toFixed(0),
      annualCashFlow: annualCashFlow.toFixed(0),
      breakEvenOccupancy: breakEvenOccupancy.toFixed(1),
      totalCashReturn: Math.round(totalCashReturn * 10) / 10,
      returnOnInvestment: Math.round(returnOnInvestment * 10) / 10,
      effectiveRent: grossIncome.toFixed(0),
      totalOperatingExpenses: totalOperatingExpenses.toFixed(0)
    };
  };

  const metrics = calculateAdvancedMetrics();

  if (!metrics) return null;

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    return num < 0 
      ? `-$${Math.abs(num).toLocaleString()}` 
      : `$${num.toLocaleString()}`;
  };

  const getCashFlowColor = (value) => {
    const num = parseFloat(value);
    if (num > 200) return 'positive';
    if (num > 0) return 'neutral';
    return 'negative';
  };

  return (
    <div className="advanced-roi-metrics">
      <div className="advanced-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <div className="premium-badge">
            <span className="badge-icon">⭐</span>
            <span>Premium Analytics</span>
          </div>
          <h3>Advanced ROI Analysis</h3>
        </div>
        <div className="expand-toggle">
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
          <span className="toggle-text">
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="advanced-content">
          <div className="metrics-grid">
            <div className="metric-category">
              <h4>Property Ratios</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Gross Rent Multiplier</span>
                  <span className="metric-value">{metrics.grossRentMultiplier}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Rent-to-Price Ratio</span>
                  <span className="metric-value">{metrics.rentToPrice}%</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Operating Expense Ratio</span>
                  <span className="metric-value">{metrics.operatingExpenseRatio}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Cash Flow Analysis</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Monthly Cash Flow</span>
                  <span className={`metric-value ${getCashFlowColor(metrics.monthlyCashFlow)}`}>
                    {formatCurrency(metrics.monthlyCashFlow)}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Annual Cash Flow</span>
                  <span className={`metric-value ${getCashFlowColor(metrics.annualCashFlow)}`}>
                    {formatCurrency(metrics.annualCashFlow)}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Cash Return</span>
                  <span className="metric-value">{metrics.totalCashReturn}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Risk Analysis</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Break-even Occupancy</span>
                  <span className="metric-value">{metrics.breakEvenOccupancy}%</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Debt Service Coverage</span>
                  <span className="metric-value">
                    {typeof metrics.debtServiceCoverageRatio === 'number' ? 
                      `${metrics.debtServiceCoverageRatio}x` : 
                      metrics.debtServiceCoverageRatio}
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Return on Investment</span>
                  <span className="metric-value">{metrics.returnOnInvestment}%</span>
                </div>
              </div>
            </div>

            <div className="metric-category">
              <h4>Income & Expenses</h4>
              <div className="metrics-row">
                <div className="metric-item">
                  <span className="metric-label">Effective Annual Rent</span>
                  <span className="metric-value">{formatCurrency(metrics.effectiveRent)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Operating Expenses</span>
                  <span className="metric-value">{formatCurrency(metrics.totalOperatingExpenses)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Net Operating Income</span>
                  <span className="metric-value">
                    {formatCurrency(parseFloat(metrics.effectiveRent) - parseFloat(metrics.totalOperatingExpenses))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="insights-section">
            <h4>Key Insights</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-icon">📊</div>
                <div className="insight-content">
                  <h5>Cash Flow Status</h5>
                  <p>
                    {parseFloat(metrics.monthlyCashFlow) > 0 
                      ? `Positive cash flow of ${formatCurrency(metrics.monthlyCashFlow)}/month` 
                      : `Negative cash flow requiring ${formatCurrency(Math.abs(metrics.monthlyCashFlow))}/month`}
                  </p>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">🎯</div>
                <div className="insight-content">
                  <h5>Occupancy Risk</h5>
                  <p>
                    {parseFloat(metrics.breakEvenOccupancy) < 85 
                      ? "Low risk - comfortable occupancy buffer" 
                      : "Higher risk - requires high occupancy to break even"}
                  </p>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">💰</div>
                <div className="insight-content">
                  <h5>Investment Efficiency</h5>
                  <p>
                    {parseFloat(metrics.grossRentMultiplier) < 12 
                      ? "Good deal - rent covers costs well" 
                      : "Consider negotiating price or finding higher rent"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedROIMetrics;